import { useState, useRef, useCallback, type MouseEvent } from 'react';
import { toJpeg } from 'html-to-image';
import {
  PencilSimple as PencilIcon,
  ArrowUpRight as ArrowUpRightIcon,
  RectangleDashed as RectangleIcon,
  MapPin as MapPinIcon,
  TextAa as TextAaIcon,
  ArrowCounterClockwise as UndoIcon,
} from '@phosphor-icons/react';
import { T, Var, msg, useGT, useMessages } from 'gt-react';
import { Button } from 'src/components/ui/button';

export type Tool = 'pen' | 'arrow' | 'rect' | 'point' | 'text';

export interface Stroke {
  id: number;
  /** Opaque id of a stored mark this stroke was seeded from. Editors can use it to preserve
   * domain metadata that the generic Annotator does not understand. */
  sourceAnnotationId?: string;
  tool: Tool;
  color: string;
  path?: { x: number; y: number }[];
  from?: { x: number; y: number };
  to?: { x: number; y: number };
  x?: number;
  y?: number;
  n?: number;
  text?: string;
}

const TOOLS: {
  id: Tool;
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
}[] = [
  { id: 'pen', Icon: PencilIcon, label: msg('Pen') },
  { id: 'arrow', Icon: ArrowUpRightIcon, label: msg('Arrow') },
  { id: 'rect', Icon: RectangleIcon, label: msg('Rectangle') },
  { id: 'point', Icon: MapPinIcon, label: msg('Pin') },
  { id: 'text', Icon: TextAaIcon, label: msg('Text') },
];

const COLORS = [
  'oklch(0.62 0.2 25)',
  'oklch(0.7 0.18 80)',
  'oklch(0.55 0.2 290)',
  'oklch(0.55 0.18 145)',
  'oklch(0.2 0 0)',
];

const COLOR_LABELS: Record<string, string> = {
  'oklch(0.62 0.2 25)': msg('Red'),
  'oklch(0.7 0.18 80)': msg('Yellow'),
  'oklch(0.55 0.2 290)': msg('Purple'),
  'oklch(0.55 0.18 145)': msg('Green'),
  'oklch(0.2 0 0)': msg('Black'),
};

interface AnnotatorProps {
  children: React.ReactNode;
  onSave?: (strokes: Stroke[]) => void;
  /** Given the composited stage (children + drawn marks) flattened to a `data:` URI. When set, the
   * primary button rasterizes the stage and hands it over — reused to bake marks onto a screenshot. */
  onExport?: (dataUri: string) => void | Promise<void>;
  /** Called if the flatten fails; the caller stays untouched (fail-open). */
  onExportError?: (err: unknown) => void;
  /** Label for the primary button (default "Send back to agent"). */
  saveLabel?: string;
  /** Marks to start from (e.g. a guide frame's stored annotations), in stage pixels. */
  initialStrokes?: Stroke[];
}

export function Annotator({
  children,
  onSave,
  onExport,
  onExportError,
  saveLabel,
  initialStrokes,
}: AnnotatorProps) {
  const gt = useGT();
  const m = useMessages();
  const [busy, setBusy] = useState(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState(COLORS[0]);
  const [strokes, setStrokes] = useState<Stroke[]>(initialStrokes ?? []);
  const [draft, setDraft] = useState<Stroke | null>(null);
  const [textInput, setTextInput] = useState<{
    x: number;
    y: number;
    value: string;
  } | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const pos = useCallback((e: MouseEvent) => {
    const el = stageRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }, []);

  function onDown(e: MouseEvent) {
    e.preventDefault();
    if (textInput) return;
    const p = pos(e);
    if (tool === 'pen') setDraft({ id: 0, tool, color, path: [p] });
    else if (tool === 'arrow') setDraft({ id: 0, tool, color, from: p, to: p });
    else if (tool === 'rect') setDraft({ id: 0, tool, color, from: p, to: p });
    else if (tool === 'point') {
      const n = strokes.filter((s) => s.tool === 'point').length + 1;
      setStrokes((s) => [
        ...s,
        { id: Date.now(), tool, color, x: p.x, y: p.y, n },
      ]);
    } else if (tool === 'text') {
      setTextInput({ x: p.x, y: p.y, value: '' });
    }
  }

  function onMove(e: MouseEvent) {
    if (!draft) return;
    const p = pos(e);
    if (draft.tool === 'pen')
      setDraft((d) => (d ? { ...d, path: [...(d.path || []), p] } : d));
    else setDraft((d) => (d ? { ...d, to: p } : d));
  }

  function onUp() {
    if (!draft) return;
    setStrokes((s) => [...s, { ...draft, id: Date.now() }]);
    setDraft(null);
  }

  function commitText() {
    if (textInput && textInput.value.trim()) {
      setStrokes((s) => [
        ...s,
        {
          id: Date.now(),
          tool: 'text',
          color,
          x: textInput.x,
          y: textInput.y,
          text: textInput.value,
        },
      ]);
    }
    setTextInput(null);
  }

  function clear() {
    setStrokes([]);
  }
  function undo() {
    setStrokes((s) => s.slice(0, -1));
  }

  async function save() {
    onSave?.(strokes);
    if (!onExport) return;
    const stage = stageRef.current;
    if (!stage) return;
    setBusy(true);
    try {
      // Rasterize the composited stage (children + drawn marks) into a flat image. pixelRatio 1
      // keeps it at display size; a screenshot is already the right resolution. toJpeg over toPng
      // to keep the inline data: URI small enough to ride the request body.
      const dataUri = await toJpeg(stage, { quality: 0.9, pixelRatio: 1 });
      await onExport(dataUri);
    } catch (err) {
      // Fail-open: leave the source untouched rather than lose it.
      onExportError?.(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border bg-white p-1.5 shadow-xs">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTool(t.id)}
            title={m(t.label)}
            aria-label={m(t.label)}
            aria-pressed={tool === t.id}
            className="flex size-[26px] items-center justify-center rounded-md border"
            style={{
              borderColor: tool === t.id ? 'var(--stone-800)' : 'transparent',
              background: tool === t.id ? 'var(--stone-100)' : 'transparent',
              color: tool === t.id ? 'var(--stone-900)' : 'var(--stone-600)',
            }}
          >
            <t.Icon className="size-3.5" />
          </button>
        ))}
        <span className="mx-0.5 h-4 w-px bg-border" />
        {COLORS.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            aria-label={m(COLOR_LABELS[c])}
            aria-pressed={color === c}
            className="size-[18px] rounded-full shadow-[0_0_0_1px_var(--stone-300)]"
            style={{
              background: c,
              border:
                color === c ? '2px solid var(--stone-900)' : '2px solid #fff',
            }}
          />
        ))}
        <div className="flex-1" />
        <button
          onClick={undo}
          disabled={!strokes.length}
          className="inline-flex h-6 items-center gap-1 rounded-[5px] border border-border bg-white px-2 text-[11px] text-stone-700 disabled:opacity-50"
        >
          <UndoIcon className="size-3" /> <T>Undo</T>
        </button>
        <button
          onClick={clear}
          disabled={!strokes.length}
          className="inline-flex h-6 items-center rounded-[5px] border border-border bg-white px-2 text-[11px] text-stone-700 disabled:opacity-50"
        >
          <T>Clear</T>
        </button>
      </div>

      {/* Stage */}
      <div
        ref={stageRef}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        className="relative cursor-crosshair select-none overflow-hidden rounded-lg"
      >
        {children}
        <svg className="pointer-events-none absolute inset-0 size-full">
          {strokes.map((s, i) => renderStroke(s, i))}
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {draft && renderStroke(draft, 'draft')}
        </svg>
        {textInput && (
          <input
            autoFocus
            value={textInput.value}
            onChange={(e) =>
              setTextInput((t) => (t ? { ...t, value: e.target.value } : t))
            }
            onBlur={commitText}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitText();
              if (e.key === 'Escape') setTextInput(null);
            }}
            className="absolute h-5 min-w-[80px] rounded-[3px] bg-white px-1 text-[11.5px] font-medium outline-none"
            style={{
              left: textInput.x,
              top: textInput.y - 14,
              color,
              border: `1.2px solid ${color}`,
            }}
            placeholder={gt('Type and press Enter')}
          />
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-muted-foreground">
          <T>
            <Var>{strokes.length}</Var> mark
            <Var>{strokes.length === 1 ? '' : 's'}</Var> on screenshot
          </T>
        </span>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={clear}>
          <T>Discard</T>
        </Button>
        <Button size="sm" onClick={save} disabled={busy}>
          {saveLabel ?? gt('Send back to agent')}
        </Button>
      </div>
    </div>
  );
}

function pathD(pts: { x: number; y: number }[]) {
  if (pts.length < 2) return `M${pts[0].x},${pts[0].y}`;
  return 'M' + pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L');
}

function renderStroke(s: Stroke, key: number | string) {
  if (s.tool === 'pen' && s.path) {
    return (
      <path
        key={key}
        d={pathD(s.path)}
        fill="none"
        stroke={s.color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  }
  if (s.tool === 'rect' && s.from && s.to) {
    const x = Math.min(s.from.x, s.to.x);
    const y = Math.min(s.from.y, s.to.y);
    const w = Math.abs(s.to.x - s.from.x);
    const h = Math.abs(s.to.y - s.from.y);
    return (
      <rect
        key={key}
        x={x}
        y={y}
        width={w}
        height={h}
        fill="none"
        stroke={s.color}
        strokeWidth="2.5"
        strokeDasharray="6 4"
        rx="3"
      />
    );
  }
  if (s.tool === 'arrow' && s.from && s.to) {
    const dx = s.to.x - s.from.x;
    const dy = s.to.y - s.from.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const ah = 10;
    const ax = s.to.x - ux * ah;
    const ay = s.to.y - uy * ah;
    const px = -uy;
    const py = ux;
    return (
      <g
        key={key}
        stroke={s.color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={s.color}
      >
        <line x1={s.from.x} y1={s.from.y} x2={ax} y2={ay} />
        <polygon
          points={`${s.to.x},${s.to.y} ${ax + px * 5},${ay + py * 5} ${ax - px * 5},${ay - py * 5}`}
          stroke="none"
        />
      </g>
    );
  }
  if (s.tool === 'point' && s.x != null && s.y != null) {
    return (
      <g key={key}>
        <circle
          cx={s.x}
          cy={s.y}
          r="11"
          fill={s.color}
          stroke="#fff"
          strokeWidth="2"
        />
        <text
          x={s.x}
          y={s.y + 3.5}
          fontSize="11"
          fontWeight="700"
          fill="#fff"
          textAnchor="middle"
        >
          {s.n}
        </text>
      </g>
    );
  }
  if (s.tool === 'text' && s.x != null && s.y != null && s.text) {
    return (
      <g key={key}>
        <rect
          x={s.x - 2}
          y={s.y - 13}
          width={s.text.length * 7 + 8}
          height="18"
          rx="3"
          fill="#fff"
          stroke={s.color}
          strokeWidth="1.2"
        />
        <text
          x={s.x + 2}
          y={s.y}
          fontSize="11.5"
          fontWeight="500"
          fill={s.color}
        >
          {s.text}
        </text>
      </g>
    );
  }
  return null;
}
